import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getSpecialist, specialistAction, streamAI, getSessions, getMessages } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Handshake, MagnifyingGlass, Megaphone, Browsers, Sparkle, PaperPlaneRight, Flame,
  CheckCircle, Clock, ArrowRight, ShieldCheck, Plus, ChatCircleDots, Wrench, TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SalesToolkit } from "@/components/SalesToolkit";
import { WebsiteAnalyzer } from "@/components/WebsiteAnalyzer";

const ICONS = { Handshake, MagnifyingGlass, Megaphone, Browsers };

function Insight({ i }) {
  return (
    <div className="card-matte p-3.5" data-testid={`specialist-insight-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="label-caps text-[10px]">{i.label}</div>
      <div className="font-head text-lg font-semibold mt-1">{i.value}</div>
    </div>
  );
}

export default function AiSpecialistPage({ specialistKey }) {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["specialist", specialistKey, activeId], queryFn: () => getSpecialist(specialistKey, activeId), enabled: !!activeId });
  const d = q.data || {};
  const p = d.profile || {};
  const Icon = ICONS[p.icon] || Sparkle;

  // --- persona chat with workspace memory (resumes latest specialist session) ---
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionsQ = useQuery({
    queryKey: ["specialist-sessions", specialistKey, activeId],
    queryFn: () => getSessions(activeId, specialistKey), enabled: !!activeId,
  });

  useEffect(() => { setSessionId(null); setMessages([]); }, [activeId, specialistKey]);
  useEffect(() => {
    const latest = (sessionsQ.data || [])[0];
    if (latest && !sessionId && messages.length === 0) {
      getMessages(latest.id).then((msgs) => { if (msgs.length) { setSessionId(latest.id); setMessages(msgs); } });
    }
  }, [sessionsQ.data]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    let sid = sessionId;
    try {
      await streamAI("/ai/chat-stream", { client_id: activeId, session_id: sessionId, message: msg, persona: specialistKey },
        (chunk) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + chunk }; return c; }),
        (meta) => { if (meta.session_id) sid = meta.session_id; });
    } catch (e) {
      const errMsg = e?.message || "Something went wrong — try again.";
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: errMsg }; return c; });
      toast.error(errMsg);
    }
    setBusy(false);
    if (sid && sid !== sessionId) setSessionId(sid);
    qc.invalidateQueries({ queryKey: ["specialist-sessions", specialistKey, activeId] });
  };
  const newChat = () => { setSessionId(null); setMessages([]); };

  const act = useMutation({
    mutationFn: (r) => specialistAction(specialistKey, { client_id: activeId, title: r.title, why: r.why, impact: r.impact }),
    onSuccess: () => { toast.success("Queued to Approval Queue — nothing executes automatically"); qc.invalidateQueries({ queryKey: ["specialist", specialistKey, activeId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to queue"),
  });

  const tasks = d.tasks || {};

  return (
    <PageContainer>
      {/* Profile header */}
      <div className="card-matte p-5 mb-5" data-testid={`specialist-profile-${specialistKey}`}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-14 w-14 rounded-2xl bg-[#6B3DFF]/15 flex items-center justify-center brand-glow shrink-0">
            <Icon size={28} className="brand-text" weight="fill" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-head text-xl font-semibold">{p.name || "AI Specialist"}</h1>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">● ACTIVE</span>
            </div>
            <div className="text-xs brand-text font-semibold mt-0.5">{p.title}</div>
            <p className="text-sm text-[#9a9a9a] mt-1.5 max-w-2xl">{p.purpose}</p>
            <div className="text-[11px] text-[#7a7a7a] mt-1.5"><span className="text-[#cfcfcf] font-semibold">Specialty:</span> {p.specialty}</div>
          </div>
          <div className="flex flex-col gap-1.5 text-xs shrink-0" data-testid="specialist-task-panel">
            <div className="flex items-center gap-1.5 text-amber-300"><Clock size={13} /> {tasks.pending ?? 0} pending in queue</div>
            <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle size={13} /> {tasks.completed ?? 0} completed</div>
            <button data-testid="specialist-goto-approvals" onClick={() => navigate("/approvals")}
              className="flex items-center gap-1 brand-text hover:text-[#A56CFF] mt-1">Approval Queue <ArrowRight size={12} /></button>
          </div>
        </div>
      </div>

      {/* Performance insights */}
      {(d.insights || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="specialist-insights">
          {d.insights.map((i) => <Insight key={i.label} i={i} />)}
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-5">
        {/* Chat */}
        <div className="lg:col-span-3 card-matte flex flex-col" style={{ minHeight: 520, maxHeight: 640 }} data-testid="specialist-chat">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex items-center gap-2 text-sm font-semibold"><ChatCircleDots size={16} className="brand-text" /> Chat with {p.name}</div>
            <button data-testid="specialist-new-chat" onClick={newChat} className="flex items-center gap-1 text-[11px] text-[#9a9a9a] hover:text-white"><Plus size={12} /> New chat</button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[#9a9a9a] mb-5">Ask {p.name} anything about {activeClient?.name} — it knows your workspace.</p>
                <div className="grid sm:grid-cols-2 gap-2 text-left">
                  {(p.prompts || []).map((s, i) => (
                    <button key={i} data-testid={`specialist-prompt-${i}`} onClick={() => send(s)}
                      className="card-matte p-3 text-xs text-[#cfcfcf] hover:text-white flex items-center gap-2 border border-white/8">
                      <Sparkle size={13} className="brand-text shrink-0" weight="fill" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} data-testid={`specialist-msg-${m.role}`} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <div className="h-7 w-7 rounded-lg bg-[#6B3DFF]/15 flex items-center justify-center shrink-0"><Flame size={14} className="brand-text" weight="fill" /></div>}
                <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap max-w-[85%] ${m.role === "user" ? "bg-[#6B3DFF] text-white" : "bg-[#161616] border border-white/8 text-[#e6e6e6]"}`}>
                  {m.content || <span className="ai-thinking text-[#9a9a9a]">Thinking…</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-white/8 flex items-end gap-2">
            <Textarea data-testid="specialist-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder={`Ask your ${p.name || "specialist"}…`} className="bg-[#10101A] border-white/10 resize-none min-h-[44px]" />
            <Button data-testid="specialist-send-btn" onClick={() => send()} disabled={busy}
              className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-11 w-11 p-0 shrink-0"><PaperPlaneRight size={17} weight="fill" /></Button>
          </div>
        </div>

        {/* Right rail: recommendations + tools */}
        <div className="lg:col-span-2 space-y-5">
          <div data-testid="specialist-recommendations">
            <div className="label-caps mb-2.5 flex items-center gap-1"><Sparkle size={13} className="brand-text" weight="fill" /> Recommended Actions</div>
            <div className="space-y-2.5">
              {(d.recommendations || []).map((r, i) => (
                <div key={i} className="card-matte p-3.5" data-testid={`specialist-rec-${i}`}>
                  <div className="font-semibold text-sm">{r.title}</div>
                  <div className="text-xs text-[#9a9a9a] mt-1">{r.why}</div>
                  {r.impact && <div className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1"><TrendUp size={11} /> {r.impact}</div>}
                  <Button data-testid={`specialist-rec-queue-${i}`} size="sm" onClick={() => act.mutate(r)} disabled={act.isPending}
                    className="mt-2.5 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-7 text-xs">Queue to Approval</Button>
                </div>
              ))}
              {!(d.recommendations || []).length && !q.isLoading && (
                <div className="card-matte p-4 text-xs text-[#7a7a7a]">No data-driven recommendations yet — connect Shopify / add data for this workspace, or ask in chat.</div>
              )}
            </div>
          </div>

          <div data-testid="specialist-tools">
            <div className="label-caps mb-2.5 flex items-center gap-1"><Wrench size={13} className="brand-text" /> Works With</div>
            <div className="flex flex-wrap gap-2">
              {(p.tools || []).map((t) => (
                <button key={t.route} data-testid={`specialist-tool-${t.route.replace(/\//g, "")}`} onClick={() => navigate(t.route)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-white/12 text-[#cfcfcf] hover:text-white hover:border-[#6B3DFF]/40 flex items-center gap-1.5">
                  {t.label} <ArrowRight size={11} />
                </button>
              ))}
            </div>
          </div>

          {(tasks.recent || []).length > 0 && (
            <div data-testid="specialist-recent-tasks">
              <div className="label-caps mb-2.5">Recent Tasks</div>
              <div className="space-y-1.5">
                {tasks.recent.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-xs card-matte px-3 py-2">
                    <span className="text-[#cfcfcf] truncate mr-2">{t.title}</span>
                    <span className={`shrink-0 font-semibold ${t.status === "published" || t.status === "approved" ? "text-emerald-400" : t.status === "pending" ? "text-amber-300" : "text-[#7a7a7a]"}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] text-[#7a7a7a]"><ShieldCheck size={13} className="brand-text" /> Approval-first: every action goes to the Approval Queue. Nothing executes automatically.</div>
        </div>
      </div>

      {specialistKey === "sales" && <SalesToolkit />}
      {specialistKey === "website" && <WebsiteAnalyzer />}
    </PageContainer>
  );
}
